import React, { useContext, useMemo } from 'react';
import styled from 'styled-components';

import MetricListContent from 'components/MetricListGadget/MetricListContent/MetricListContent';
import GqlClientProvider from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/GqlClientProvider';
import DraftSavedFiltersContext from 'contexts/DraftSavedFiltersContext';
import MetricOptionsContext from 'contexts/MetricOptionsContext';

import { toPersistedComparison } from './blockComparison';
import { buildMetricListDefinition } from './buildMetricListDefinition';
import ChartPlaceholder from './ChartPlaceholder';
import LegacyGadgetBody from './LegacyGadgetBody';

const Content = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

// A metric list Table (a block): drawn by the legacy Metric List's rows, as
// it saves, within its dates and against its comparison. Its items read
// their filters from draft saved filters (DraftSavedFiltersContext), so a
// change shows before it's saved. Stable while its settings don't change,
// so it doesn't refetch on a move.
const StudioMetricList = ({
  content,
  name,
  isInteractive,
}: {
  content: Studio.ChartContent;
  name: string;
  isInteractive: boolean;
}) => {
  const { metricOptionsLookup } = useContext(MetricOptionsContext);
  const settingsKey = JSON.stringify([content.table, content.comparison]);
  const built = useMemo(
    () =>
      buildMetricListDefinition({
        content: { visType: 'table', table: content.table },
        id: content.id,
        name,
        resolveName: (metricId) => {
          const metric = metricOptionsLookup[metricId];
          return metric ? metric.name : '';
        },
      }),
    // The settings are compared by value
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [content.id, metricOptionsLookup, name, settingsKey],
  );
  const comparison = useMemo(
    () =>
      content.comparison
        ? toPersistedComparison(content.comparison)
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [settingsKey],
  );

  if (!('chartDefinition' in built)) {
    return (
      <ChartPlaceholder
        cardId={content.id}
        visType="table"
        message={built.missing}
      />
    );
  }
  const { chartDefinition: list, draftSavedFilters } = built;
  return (
    <LegacyGadgetBody
      $isInteractive={isInteractive}
      data-testid={`studio-card-metric-list-${content.id}`}
    >
      <div>
        <Content>
          <DraftSavedFiltersContext.Provider value={draftSavedFilters}>
            <GqlClientProvider
              visName={list.name}
              visId={list.id}
              visType="metricList"
            >
              <MetricListContent
                orderedMetricList={list.list}
                comparison={comparison}
                isBonusPeriodMode={false}
              />
            </GqlClientProvider>
          </DraftSavedFiltersContext.Provider>
        </Content>
      </div>
    </LegacyGadgetBody>
  );
};

export default StudioMetricList;
